import { Link } from "react-router-dom";
import { ArrowRight, Compass, Heartbeat, Cube, Lightning } from "@phosphor-icons/react";

const VALUES = [
  { n: "01", t: "Craft over Volume", d: "We staff fewer projects with senior squads. Outcomes over headcount." , icon: Cube },
  { n: "02", t: "Structural Thinking", d: "We design systems, not features. Every layer is intentional.", icon: Compass },
  { n: "03", t: "Human-first Tech", d: "Software should reduce friction for the people using it.", icon: Heartbeat },
  { n: "04", t: "Bias for Shipping", d: "Weekly demos. Weekly deploys. Compounding velocity.", icon: Lightning },
];

const TIMELINE = [
  { year: "2018", t: "Founded in Bengaluru", d: "Two engineers and a whiteboard." },
  { year: "2020", t: "First international squad", d: "Embedded with a Singapore fintech." },
  { year: "2022", t: "Crossed 50 ships", d: "Across SaaS, fintech, and healthcare." },
  { year: "2024", t: "AI Practice launched", d: "Production RAG and MLOps for 18 clients." },
  { year: "2025", t: "Global studio", d: "Engineers in 11 countries. One team." },
];

const TEAM = [
  { name: "Riya Sharma", role: "Co-founder, CEO", img: "https://images.pexels.com/photos/36733304/pexels-photo-36733304.jpeg" },
  { name: "Arjun Vyas", role: "Co-founder, CTO", img: "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg" },
  { name: "Mei Tan", role: "Head of Design", img: "https://images.pexels.com/photos/27301621/pexels-photo-27301621.png" },
  { name: "David Okoro", role: "Head of Cloud", img: "https://images.pexels.com/photos/12651142/pexels-photo-12651142.jpeg" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="border-b border-black/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 relative">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00] reveal-up">/ About </p>
          <div className="grid grid-cols-12 gap-6 mt-6">
            <h1 className="font-display col-span-12 md:col-span-10 text-mega font-black tracking-tighter uppercase leading-[0.9] reveal-up">
              An engineering<br/>studio built on<br/><span className="text-[#FF4F00]">craft and cadence.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ Story</p>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
              Built like a studio.<br/>Operated like a team.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-[#0A0A0A]">
              <span className="font-display text-7xl float-left mr-3 leading-none text-[#FF4F00]">U</span>
              CUBE Technologies started in 2018 with a quiet conviction: that the best software comes from small, senior teams that work close to the problem. Seven years on, that conviction has held. We've shipped 120+ products across SaaS, fintech, healthcare, climate, and logistics — and we still operate as one studio, not a vendor.
            </p>
            <p className="mt-6 text-[#52525B] leading-relaxed">
              We measure ourselves on what gets shipped, what stays running, and what your team learns. No bench-warmers. No hand-offs. No theatre.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-black/10 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ Values</p>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
                Four principles<br/>we don't bend.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.n} className="bg-[#F4F4F5] p-10 hover:bg-white transition-colors">
                  <div className="flex items-start justify-between">
                    <span className="font-display text-6xl md:text-7xl font-black text-[#0A0A0A]">{v.n}</span>
                    <Icon size={28} weight="duotone" className="text-[#FF4F00]" />
                  </div>
                  <h3 className="font-display mt-8 text-2xl font-bold uppercase tracking-tight">{v.t}</h3>
                  <p className="mt-3 text-[#52525B] text-sm leading-relaxed max-w-md">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ Trajectory</p>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
                Seven years.<br/>Compounding craft.
              </h2>
            </div>
          </div>
          <ol className="relative border-l-2 border-[#0A0A0A]">
            {TIMELINE.map((e) => (
              <li key={e.year} className="pl-8 pb-12 last:pb-0 relative">
                <span className="absolute left-[-9px] top-1 w-4 h-4 bg-[#FF4F00]" />
                <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#52525B]">{e.year}</p>
                <p className="font-display mt-2 text-2xl md:text-3xl font-bold tracking-tight">{e.t}</p>
                <p className="mt-2 text-[#52525B]">{e.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TEAM */}
      <section className="border-b border-black/10 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ The Studio</p>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
                A senior bench.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {TEAM.map((m) => (
              <div key={m.name} data-testid={`team-card-${m.name.split(' ')[0].toLowerCase()}`} className="group">
                <div className="relative overflow-hidden border border-black/10 group-hover:border-[#FF4F00] transition-colors">
                  <img src={m.img} alt={m.name} className="w-full aspect-[4/5] object-cover grayscale group-hover:grayscale-0 transition duration-500" />
                </div>
                <p className="font-display mt-4 text-lg font-bold tracking-tight">{m.name}</p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#52525B] mt-1">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              Work with us.
            </h2>
            <p className="mt-4 text-white/70 max-w-md">Tell us about your team and the problem you're solving.</p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link to="/contact" data-testid="about-cta-button" className="inline-flex items-center gap-3 bg-[#FF4F00] px-8 py-5 text-sm font-bold uppercase tracking-[0.2em] hover:bg-[#E64700]">
              Get in Touch <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
